import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ClockService, ColumnKey, Complaint, ComplaintPosition, ComplaintPriorities, ComplaintUserType, DocumentStatus, SalesDocument, scrollTop } from '@icc/b2b/shared';
import { ComplaintService } from '../complaint.service';
import { Router } from '@angular/router';
import { TranslateService } from '@icc/shared';

@Component({
  selector: 'icc-complaint-list-table',
  templateUrl: './complaint-list-table.component.html',
  styleUrls: ['./complaint-list-table.component.scss']
})
export class ComplaintListTableComponent {
    @Output() updateDocuments = new EventEmitter<boolean>();
    @Output() saveConfiguration = new EventEmitter<void>();
    @Output() setColumnSortOptions = new EventEmitter<void>();
    @Output() changeOrderBy = new EventEmitter<ColumnKey>();

    @Input() filteredDocuments: Complaint[] = [];
    @Input() orderedDocuments: Complaint[] = [];
    @Input() viewComplaintOrderNumber = false;
    @Input() userType: ComplaintUserType = 'authorizedRetailer';

    documentStatuses: DocumentStatus[] = [];
    showDocumentStatuses = true;

    checkedDocuments: Record<string, boolean> = {};
    showGroupOptions = false;

    documentActionsAccess = new Map<
        string,
        {
            select: boolean;
            order: boolean;
            copy: boolean;
            transferChanges: boolean;
        }
    >();

    @Input() columnSortOptions: Partial<
        Record<
            ColumnKey,
            {
                order: 'asc' | 'desc' | null;
                index: number;
            } | null
        >
    > = {};
    @Input() mode: 'selection' | 'index' = 'index';

    @Input() documentsInPage = 20;
    @Input() totalItems = 0;

    private _page = 1;
    @Input()
    get page(): number {
        return this._page;
    }
    set page(page: number) {
        this._page = page;
    }

    @Output() currentPageEmitter = new EventEmitter<number>();

    onChangePage(page: number) {
        this.page = page;
        this.currentPageEmitter.emit(page);
        this.saveConfiguration.emit();
        this.updateDocuments.emit(true);
        scrollTop();
    }

    get loaded() {
        return ClockService.getClock()?.timestamp != null;
    }

    localDocumentsResolve: () => void;
    loadedLocalDocuments: Promise<void>;

    constructor(
        private router: Router,
        private complaintService: ComplaintService,
        private translateService: TranslateService
    ) {}

    open(document: Complaint) {
        this.complaintService.openDocument(document);
        this.router.navigate(['trade', 'complaint', document.uuid], { queryParams: { origin: 'list' } });
    }

    orderBy(column: ColumnKey) {
        this.changeOrderBy.emit(column);
    }

    onChecked() {
        const checkedDocuments = Object.entries(this.checkedDocuments)
            .filter(([, checked]) => checked)
            .map(([uuid]) => uuid);
        this.showGroupOptions = checkedDocuments.length > 0;
    }

    hasOneStatus(documents: SalesDocument[]) {
        const firstStatus = documents[0].statusId;

        for (const document of documents) {
            if (document.statusId !== firstStatus) {
                return false;
            }
        }
        return true;
    }

    trackByUuid(index: number, item: Complaint): string {
        return item.uuid;
    }

    getPriority(document: Complaint) {
        const rawPriority = this.complaintService.getPriority(document);
        if (rawPriority) {
            const priorityGroup = ComplaintPriorities[+rawPriority.slice(0, 1)];
            const priorityValue = rawPriority.slice(1);
            return this.translateService.instant(priorityGroup) + '-' + priorityValue;
        }
        return '';
    }

    getAssignedTo(document: Complaint) {
        return this.complaintService.getAssignedTo(document, document.assignedTo, document.distributorCompanyId);
    }

    getAssignedToUserName(document: Complaint) {
        return this.complaintService.getAssignedToUserName(document, document.assignedTo);
    }

    getStatus(document: Complaint) {
        let statusField = (document.assignedTo || '').split('_')[0] + 'StatusId' as 'authorizedRetailerStatusId' | 'distributorStatusId' | 'manufacturerStatusId';
        if (this.userType === 'authorizedRetailer' && document.assignedTo === 'manufacturer') {
            statusField = 'distributorStatusId';
        }
        return this.complaintService.getStatus(document[statusField] || 0);
    }

    getUniqueDistributorOrderNumbers(document: Complaint) {
        return document.distributorOrderNumbers;
    }
}
